"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const Map_1 = __importDefault(require("ol/Map"));
const View_1 = __importDefault(require("ol/View"));
const Overlay_1 = __importDefault(require("ol/Overlay"));
const projections_1 = require("./projections");
const LayerControl_1 = __importDefault(require("./LayerControl"));
const utils_1 = require("./utils");
const defaultMapOptions = {
    // Initial zoom level
    zoom: 4,
    // Fit view on initial load (overrides zoom and center)
    fitView: true,
    // Should a popup slide map so it fits the popup
    autoPan: false,
    // Radius in pixels around mouse position where features should be selected for popup
    hitTolerance: 5,
};
class OLWrapper {
    constructor({ mapRootElement, projection, tileLayers = [], controls = [], popupTemplate, mapOptions }) {
        this.projection = projection;
        this.tileLayers = tileLayers;
        this.controls = controls;
        this.mapOptions = { ...defaultMapOptions, ...mapOptions };
        this.viewParams = (0, projections_1.getViewParams)(projection.getCode());
        this.popupOverlay = popupTemplate
            ? popupTemplate.popupOverlay ?? new Overlay_1.default({
                element: popupTemplate.popupElement,
                autoPan: this.mapOptions.autoPan,
            })
            : undefined;
        const overlays = this.popupOverlay ? [this.popupOverlay] : [];
        const baseMaps = tileLayers.filter(l => l.get('layerType') === 'baseMap');
        const view = new View_1.default({
            projection: this.projection,
            center: this.mapOptions.center ?? this.viewParams.initCenter,
            zoom: this.mapOptions.zoom,
            showFullExtent: true
        });
        this.map = new Map_1.default({
            target: mapRootElement,
            view,
            layers: baseMaps,
            overlays,
            controls: this.controls
        });
        if (this.mapOptions.fitView) {
            view.fit(this.viewParams.extent);
        }
    }
    destroyMap() {
        this.map.setTarget(undefined);
    }
    addGeoJson(layer, epsgCodeForData, projection, extent) {
        const vectorLayer = (0, utils_1.geoJsonToLayer)(layer, epsgCodeForData, projection, extent);
        this.map.addLayer(vectorLayer);
        return vectorLayer;
    }
    addPoints(layer) {
        const vectorLayer = (0, utils_1.pointsToLayer)(layer, this.viewParams.extent);
        this.map.addLayer(vectorLayer);
        return vectorLayer;
    }
    updatePoints(layer, layerFilter) {
        this.removeLayers(layerFilter);
        this.addPoints(layer);
    }
    removeLayers(layerFilter) {
        this.map.getLayers()
            .getArray()
            .filter(layerFilter)
            .forEach(layer => this.map.removeLayer(layer));
    }
    addToggleLayers(toggleLayers) {
        return toggleLayers.reduce((acc, tl) => {
            if (tl.geoType === 'point') {
                this.removeLayers((layer) => layer.get('id') === tl.id);
                acc.push(this.addPoints(tl));
            }
            else if (tl.geoType === 'geojson') {
                this.removeLayers((layer) => layer.get('id') === tl.id);
                acc.push(this.addGeoJson(tl, 'EPSG:4326', this.projection, this.viewParams.extent));
            }
            return acc;
        }, []);
    }
    get baseMaps() {
        return (0, utils_1.findLayers)(this.map, (l) => l.get('layerType') === 'baseMap');
    }
    getToggleLayers(returnOnlyVisible) {
        return (0, utils_1.findLayers)(this.map, (l) => l.get('layerType') === 'toggle' && (!returnOnlyVisible || l.getVisible()));
    }
    set attributionUpdater(copyright) {
        if (!copyright.isInitialized)
            return;
        const view = this.map.getView();
        const tileLayers = this.tileLayers;
        const layerSwitcher = this.controls.find(control => control instanceof LayerControl_1.default);
        if (!layerSwitcher)
            return;
        copyright.updateAttribution(view, tileLayers);
        layerSwitcher.on('change', _ => {
            copyright.updateAttribution(view, tileLayers);
        });
        this.map.on('moveend', _ => {
            copyright.updateAttribution(view, tileLayers);
        });
    }
}
exports.default = OLWrapper;
